import {Component, Mixins} from 'vue-property-decorator'
import {mapGetters} from 'vuex'

import WebpartTemplate from '@webparts/components/webparts/base/WebpartTemplate.vue'
import WebpartBase from '@webparts/components/webparts/base/WebpartBase'
import {LoadingBar} from '@appbase/components/loading'
import NoData from '@webparts/components/common/NoData.vue'
import api from "@webparts/api";

import * as accountTypes from '@account/store/types'
import Vue from "vue";
import {
    IEDate,
    uGetInDateBoundaryEqual,
    uGetIsInDateTime,
    uGetIsInDateTime2
} from "@appbase/util/date-helper";
import {getGUID} from "@appbase/util";

@Component({
    name: 'dept-calendar-webpart',
    components: {
        WebpartTemplate, LoadingBar, NoData
    },
    computed: {
        ...mapGetters({
            user: accountTypes.USER
        })
    }
})
export default class DeptCalendarWebpart extends Mixins(WebpartBase) {

    user!: account.TUser

    schedules: calendar.UserSchedule[] = [];

    colors: string[] = ['yellow', 'green', 'blue', 'purple', 'red'];
    hours: string[] = ['07', '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21']

    async load() {
        const _this = this;
        this.loading = true

        try {
            let result = await api.home.getDeptUserSchedules();
            this.schedules = result.map((item, idx) => {
                return {
                    ...item,
                    schedule: {
                        ...item.schedule,
                        scheduleItems: item.schedule.scheduleItems.map(si => {
                            return {
                                ...si,
                                id: getGUID(false)
                            }
                        })
                    },
                    color: _this.colors[idx % 5]
                } as calendar.UserSchedule
            })
        } catch (error) {
            console.error(error)
        }

        this.loading = false
    }

    onClickSchedule(userSchedule: calendar.ScheduleItem[], hour: string, event: any) {
        const scheduleItem = this.findScheduleItem(userSchedule, hour)
        if (scheduleItem) {
            let ref = this.$refs[scheduleItem.id] as any;
            if (ref) {
                // 배열인 경우 중간 요소 사용
                let tooltipEl = Array.isArray(ref) && ref.length > 0 ? ref[Math.floor(ref.length / 2)] : ref;

                if (!tooltipEl) return;

                // 툴팁 표시 전에 위치 재계산
                this.positionTooltipOverActiveSchedule(tooltipEl);

                // 툴팁 표시
                tooltipEl.style.display = 'block';

                // 이벤트 전파 중지
                if (event) {
                    event.stopPropagation();
                }

                // 다른 툴팁 숨기기
                document.querySelectorAll('.schedule-tooltip').forEach((el: Element) => {
                    if (el !== tooltipEl) {
                        (el as HTMLDivElement).style.display = 'none';
                    }
                });

                // 클릭 외부 영역에서 툴팁 숨기기
                setTimeout(() => {
                    const clickHandler = (e: MouseEvent) => {
                        // 클릭한 요소가 현재 툴팁이나 그 하위 요소가 아닌 경우에만 툴팁 숨김
                        if (tooltipEl && !tooltipEl.contains(e.target as Node)) {
                            tooltipEl.style.display = 'none';
                            document.removeEventListener('click', clickHandler);
                        }
                    };
                    document.addEventListener('click', clickHandler);
                }, 0);
            }
        }
    }

    // .on 요소 위에 툴팁 위치시키는 함수
    positionTooltipOverActiveSchedule(tooltipEl: HTMLElement) {
        const parentCell = tooltipEl.closest('td');
        if (!parentCell) return;

        // 해당 셀 내의 .on 클래스를 가진 요소들 찾기
        const onElements = parentCell.querySelectorAll('.on');

        if (onElements.length > 0) {
            // .on 요소들의 중앙 위치 계산
            let leftPosition = 0;

            onElements.forEach((onEl: Element) => {
                // 각 .on 요소의 위치 누적
                const rect = onEl.getBoundingClientRect();
                const cellRect = parentCell.getBoundingClientRect();
                leftPosition += (rect.left + rect.width / 2) - cellRect.left;
            });

            // .on 요소들의 중앙 위치 계산
            leftPosition = leftPosition / onElements.length;

            // 툴팁 위치 설정
            tooltipEl.style.left = `${leftPosition}px`;
            tooltipEl.style.transform = 'translateX(-50%)';
        } else {
            // .on 요소가 없는 경우 셀 중앙에 배치
            tooltipEl.style.left = '50%';
            tooltipEl.style.transform = 'translateX(-50%)';
        }
    }

    // 일정 제목 가져오기
    getScheduleTitle(userSchedule: calendar.ScheduleItem[], hour: string): string {
        // 해당 시간에 맞는 일정 찾기
        const scheduleItem = this.findScheduleItem(userSchedule, hour)
        return scheduleItem ? scheduleItem.isPrivate ? this.$t('비공개일정').toString() : scheduleItem.subject : ''
    }

    getScheduleId(userSchedule: calendar.ScheduleItem[], hour: string): string {
        const scheduleItem = this.findScheduleItem(userSchedule, hour)
        return scheduleItem ? scheduleItem.id : ''
    }

    // 해당 시간에 일정이 있는지 확인
    hasSchedule(userSchedule: calendar.ScheduleItem[], hour: string): boolean {
        return this.findScheduleItem(userSchedule, hour) !== null &&
            (this.hasScheduleFirstHalf(userSchedule, hour) || this.hasScheduleSecondHalf(userSchedule, hour));
    }

    getStartHourDate(hour: string): Date {
        return this.getHourDate(hour, 0, 0);
    }

    getEndHourDate(hour: string): Date {
        return this.getHourDate(hour, 59, 59);
    }

    getHalfHourDate(hour: string): Date {
        return this.getHourDate(hour, 30, 0);
    }

    isHourInScheduleBoundary(hour: string, start: Date, end: Date) {
        let curHourStart = this.getStartHourDate(hour)
        let curHourEnd = this.getEndHourDate(hour)

        return uGetInDateBoundaryEqual(curHourStart, curHourEnd, IEDate(start), IEDate(end));
    }

    isHourInScheduleDate(hour: string, start: Date, end: Date) {
        let curHourStart = this.getStartHourDate(hour)

        return uGetIsInDateTime2(curHourStart, IEDate(start), IEDate(end))
    }

    setPriorOnSchedule(hour: string, start: Date, end: Date): number {
        let isInBoundaryEqual = this.isHourInScheduleBoundary(hour, start, end)
        let isInDateTime = this.isHourInScheduleDate(hour, start, end)

        return (isInDateTime && isInBoundaryEqual) ? -1 : (isInBoundaryEqual && !isInDateTime) ? 1 : 0;
    }

    findScheduleItems(userSchedule: calendar.ScheduleItem[], hour: string): calendar.ScheduleItem[] {
        const _this = this;

        return userSchedule.filter(item => {
            var _end = new Date(item.end.dateTime)
            _end = new Date(_end.setTime(_end.getTime()-1000))
            return _this.isHourInScheduleBoundary(hour, item.start.dateTime, _end)
            || _this.isHourInScheduleDate(hour, item.start.dateTime, _end)
        });
    }

    // 해당 시간에 맞는 일정 찾기
    findScheduleItem(userSchedule: calendar.ScheduleItem[], hour: string): calendar.ScheduleItem | null {
        const _this = this;
        const items = this.findScheduleItems(userSchedule, hour);
        // console.debug('findScheduleItem', hour, items);

        return items.sort((aItem, bItem) =>
            this.setPriorOnSchedule(hour, aItem.start.dateTime, aItem.end.dateTime)
            - this.setPriorOnSchedule(hour, bItem.start.dateTime, bItem.end.dateTime))[0] ?? null;
    }

    // 해당 시간 전반부에 일정이 있는지 확인
    // ex) 10:00 ~ 10:30
    hasScheduleFirstHalf(userSchedule: calendar.ScheduleItem[], hour: string): boolean {
        const schedules = this.findScheduleItems(userSchedule, hour);
        const start = this.getStartHourDate(hour);
        const end = this.getHalfHourDate(hour);

        return schedules.some(scheduleItem =>
            uGetIsInDateTime(start, IEDate(scheduleItem.start.dateTime), IEDate(scheduleItem.end.dateTime)) &&
            uGetIsInDateTime(end, IEDate(scheduleItem.start.dateTime), IEDate(scheduleItem.end.dateTime))
        );
    }

    // 해당 시간 후반부에 일정이 있는지 확인
    // ex) 10:30 ~ 11:00
    hasScheduleSecondHalf(userSchedule: calendar.ScheduleItem[], hour: string): boolean {
        const schedules = this.findScheduleItems(userSchedule, hour);
        const start = this.getHalfHourDate(hour);
        const end = this.getStartHourDate(hour);
        end.setHours(Number(end.getHours()) + 1);

        return schedules.some(scheduleItem =>
            uGetIsInDateTime(start, IEDate(scheduleItem.start.dateTime), IEDate(scheduleItem.end.dateTime)) &&
            uGetIsInDateTime(end, IEDate(scheduleItem.start.dateTime), IEDate(scheduleItem.end.dateTime))
        );
    }


    created() {
        if (this.preview) {
            this.initializePreview();
        } else {
            this.registerDirectives();
            this.load();
        }
    }

    private initializePreview() {
        this.schedules = [
            {
                user: this.user,
                schedule: {
                    scheduleId: this.user.email,
                    scheduleItems: []
                },
                color: 'yellow',
            }
        ];
    }

    private registerDirectives() {
        Vue.directive('tooltip-center', {
            // inserted: (el) => TooltipManager.setTooltip(el),
            update: (el) => TooltipManager.setTooltip(el)
        });
    }

    private getHourDate(hour: string, minute: number = 0, second: number = 0): Date {
        let date = new Date();
        date.setHours(Number(hour));
        date.setMinutes(minute);
        date.setSeconds(second);
        date.setMilliseconds(0);
        return date;
    }
}